import VictronBadgeIcon from '@assets/icons/victron-badge.svg?react';
import { APP_PREFIX_PATH } from '@constants/common';
import { useAppSelector } from '@hooks/generic';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import {
    Box,
    Button,
    Fade,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Skeleton,
    Typography,
} from '@mui/material';
import { LoadableNotification } from '@notifications/types';
import { selectLocale } from '@store/translationSlice';
import { getDateFnsLocale } from '@utils/common';
import { formatDistanceToNowStrict } from 'date-fns';
import { motion, useAnimate, useMotionValue, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { RowComponentProps } from 'react-window';

const getNotificationUrl = (notification: LoadableNotification) => {
    switch (notification.type) {
        case 'new_badge_earned':
            return `${APP_PREFIX_PATH}/accounts/details/my-events?tab=earned-badges`;
        case 'event_invitation':
        case 'event_reminder':
            return `${APP_PREFIX_PATH}/events/${notification.relevant_data?.['event_type']}/${notification.relevant_data?.['event_id']}`;
        default:
            return APP_PREFIX_PATH;
    }
};

export const NotificationItemCard = ({
    index,
    style,
    notifications,
    hasNextPage,
    onRead,
    isReadActionRunning,
}: RowComponentProps<{
    notifications: LoadableNotification[];
    hasNextPage: boolean;
    onRead?: (id: LoadableNotification['id']) => void;
    isReadActionRunning?: boolean;
}>) => {
    const [isHovering, setIsHovering] = useState(false);
    const notification = notifications[index] ?? null;
    const currentLocale = useAppSelector(selectLocale);
    const dateLocale = getDateFnsLocale(currentLocale);

    //#region Swip-to-read
    const [scope, animate] = useAnimate();
    const x = useMotionValue(0);
    const didSwipeRef = useRef(false);

    const opacity = useTransform(x, [-120, -80, 0, 80, 120], [0, 0.5, 1, 0.5, 0]);

    // Handle the end of the swipe gesture
    const handleDragEnd = (_event: any, info: any) => {
        const shouldMarkAsRead = Math.abs(info.offset.x) > 80 && onRead && !isReadActionRunning && notification;

        // If swiped left past the threshold (-80px) and action isn't running
        if (shouldMarkAsRead) {
            onRead(notification.id);
        } else {
            animate(x, 0, { type: 'spring', stiffness: 500, damping: 35 });
        }

        didSwipeRef.current = false;
    };

    const handleDrag = (_event: any, info: any) => {
        if (Math.abs(info.offset.x) > 6) {
            didSwipeRef.current = true;
        }
    };

    const handleNotificationClickCapture = (event: React.MouseEvent) => {
        if (!didSwipeRef.current) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        didSwipeRef.current = false;
    };

    useEffect(() => {
        if (notification?.id) {
            x.set(0);
            if (scope.current) {
                animate(scope.current, { x: 0 }, { duration: 0 });
            }
        }
    }, [notification?.id, x, animate, scope]);
    //#endregion

    return (
        <div style={style}>
            {!notification || notification.loading ? (
                <ListItem
                    key={notification?.id ?? `loading-${index}`}
                    secondaryAction={
                        <Skeleton>
                            <Typography fontSize={12}>Loading...</Typography>
                        </Skeleton>
                    }
                    sx={{
                        py: '7px',
                        pr: '100px',
                        height: '100%',
                    }}
                >
                    <ListItemAvatar>
                        <Skeleton variant="circular">
                            <DateRangeOutlinedIcon />
                        </Skeleton>
                    </ListItemAvatar>
                    <ListItemText
                        primary={
                            <Skeleton>
                                <Typography>Loading...</Typography>
                            </Skeleton>
                        }
                        secondary={
                            <Skeleton>
                                <Typography>Please wait...</Typography>
                            </Skeleton>
                        }
                    />
                </ListItem>
            ) : (
                <Fade in timeout={500}>
                    <motion.div
                        key={notification.id}
                        ref={scope}
                        drag="x"
                        dragDirectionLock
                        dragConstraints={{ left: -120, right: 120 }}
                        style={{
                            x,
                            position: 'relative',
                            zIndex: 1,
                            backgroundColor: 'white',
                            opacity,
                            height: '100%',
                        }}
                        onDrag={handleDrag}
                        onDragEnd={handleDragEnd}
                    >
                        <ListItem
                            divider={index < notifications.length - 1 || hasNextPage}
                            onMouseEnter={onRead ? () => setIsHovering(true) : undefined}
                            onMouseLeave={onRead ? () => setIsHovering(false) : undefined}
                            secondaryAction={
                                !isHovering ? (
                                    <Typography
                                        fontSize={12}
                                        letterSpacing={-0.562}
                                        lineHeight="12.959px"
                                        color="neutral-600"
                                    >
                                        {formatDistanceToNowStrict(new Date(notification.created_at), {
                                            addSuffix: true,
                                            locale: dateLocale,
                                        })}
                                    </Typography>
                                ) : (
                                    <Button
                                        variant="text"
                                        className="px-0"
                                        onClick={() => onRead?.(notification.id)}
                                        disabled={isReadActionRunning}
                                    >
                                        <Typography fontSize={12}>
                                            <FormattedMessage
                                                id="notifications.markAsRead"
                                                defaultMessage="Mark as read"
                                            />
                                        </Typography>
                                    </Button>
                                )
                            }
                            disablePadding
                            sx={{ height: '100%' }}
                        >
                            <ListItemButton
                                component={Link}
                                to={getNotificationUrl(notification)}
                                draggable={false}
                                onDragStart={(event) => event.preventDefault()}
                                onClickCapture={handleNotificationClickCapture}
                                sx={{
                                    py: '7px',
                                    pr: '100px !important',
                                    height: '100%',
                                }}
                            >
                                <ListItemAvatar>
                                    <Box
                                        p="10px"
                                        bgcolor="primary-700.main"
                                        width="min-content"
                                        borderRadius="50%"
                                        height="min-content"
                                    >
                                        {notification.type !== 'new_badge_earned' ? (
                                            <DateRangeOutlinedIcon
                                                sx={{
                                                    display: 'block',
                                                    fill: 'white',
                                                    fontSize: 20,
                                                }}
                                            />
                                        ) : (
                                            <VictronBadgeIcon width={20} height={20} />
                                        )}
                                    </Box>
                                </ListItemAvatar>
                                <ListItemText
                                    classes={{ primary: 'line-clamp-1', secondary: 'line-clamp-2' }}
                                    primary={
                                        <Typography
                                            fontSize={14}
                                            fontWeight={600}
                                            letterSpacing={-0.562}
                                            lineHeight="15.95px"
                                        >
                                            {notification.title}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography
                                            fontSize={12}
                                            letterSpacing={-0.562}
                                            lineHeight="12.959px"
                                            color="neutral-600"
                                            className="line-clamp-2"
                                        >
                                            {notification.summary || ''}
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        </ListItem>
                    </motion.div>
                </Fade>
            )}
        </div>
    );
};
